import React, { useEffect, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { getCoachByPlayerProfile } from '../../../../services/firestoreService';
import { getLatestCoachUpdate } from '../../../../services/coachUpdatesService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

const parseBold = (text) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold text-white">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const CoachUpdatesEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [update, setUpdate] = useState(null);
  const [coachName, setCoachName] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const coach = await getCoachByPlayerProfile(profileId);
        if (coach?.id) {
          setCoachName(coach.displayName || 'Coach');
          const latest = await getLatestCoachUpdate(coach.id);
          setUpdate(latest);
        }
      } catch (err) {
        console.error('Error loading coach update preview:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const features = BLOCK_FEATURES['coach-updates'];

  // Build preview data
  const heading = update?.heading || content.fallbackHeading || 'Latest Update';
  const body = update?.body || content.fallbackBody || 'No updates posted yet.';
  const lines = body.split('\n');
  let dateStr = null;
  if (update?.createdAt) {
    const date = update.createdAt?.toDate?.() || new Date(update.createdAt);
    dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="space-y-4">
      {/* Info note — plain text, no box */}
      <p className="text-xs text-gray-500 leading-relaxed">
        Auto-synced from your <strong className="text-gray-300">Updates</strong> tab. Manage updates in your coaching admin dashboard.
      </p>

      {/* Live preview card */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Preview</label>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            Loading preview...
          </div>
        ) : (
          <div className="relative rounded-xl border border-gray-700 bg-[#1a1a2e] p-4 sm:p-5 overflow-hidden">
            {/* Decorative icon */}
            <ClipboardList
              size={40}
              className="absolute top-3 right-3 pointer-events-none text-gray-600 opacity-20"
            />

            {/* Heading */}
            {heading && (
              <h4 className="text-base font-bold text-white mb-1">{heading}</h4>
            )}

            {/* Author + Date */}
            {(coachName || dateStr) && (
              <p className="text-xs text-gray-500 mb-3">
                {coachName && <span className="font-semibold text-gray-400">{coachName}</span>}
                {coachName && dateStr && <span> &middot; </span>}
                {dateStr}
              </p>
            )}

            {/* Body */}
            <div className="space-y-0.5">
              {lines.map((line, i) =>
                line.trim() === '' ? (
                  <div key={i} className="h-2" />
                ) : (
                  <p key={i} className="text-sm text-gray-300 leading-relaxed">
                    {parseBold(line)}
                  </p>
                )
              )}
            </div>

            {!update && (
              <p className="text-xs text-gray-600 mt-3 italic">Showing fallback — post an update to see it here</p>
            )}
          </div>
        )}
      </div>

      {/* Fallback heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Fallback Heading
        </label>
        <input
          type="text"
          value={content.fallbackHeading || ''}
          onChange={(e) => handleChange('fallbackHeading', e.target.value)}
          placeholder="e.g. Latest Update"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
        <p className="text-xs text-gray-500 mt-1">Shown when no updates have been posted yet</p>
      </div>

      {/* Fallback body */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Fallback Body
        </label>
        <textarea
          value={content.fallbackBody || ''}
          onChange={(e) => handleChange('fallbackBody', e.target.value)}
          placeholder="e.g. No updates posted yet."
          rows={3}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none font-mono text-sm leading-relaxed"
        />
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
          theme={theme || {}}
          blockType="coach-updates"
        />
      </>)}
    </div>
  );
};

export default CoachUpdatesEditor;
